import React, { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Tab } from 'react-bootstrap'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Tabs } from '../../../components/Tabs/Tabs'
import { Button } from '../../../components/Button/Button'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { IntegrationManagementHeader } from '../../Integration/IntegrationManagement/IntegrationManagementHeader'
import { OAuth2AppsPermissionsTab } from './OAuth2AppsPermissionsTab'
import { type OAuth2App } from '../../../store/auth/auth'
import { OAuth2AppsAboutTab } from './OAuth2AppsAboutTab'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { DEVELOPER_DOCS_URL } from '../constants'

export interface OAuth2AppsManagementTabsProps {
  oAuth2App: OAuth2App
}

export const OAuth2AppsManagementTabs: React.FC<OAuth2AppsManagementTabsProps> = ({ oAuth2App }) => {
  const { description } = oAuth2App
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { portalRef } = useContext(HeaderContext)
  const { hasScope } = useZocksAuth()
  const accountId = useAppSelector(selectAccountId)

  const { appId, activeTab = 'about' } = useParams<{
    appId: string
    activeTab?: string
  }>() as { appId: string; activeTab?: string }

  const isAppOwnedByAccount = oAuth2App.accountId === accountId
  const canManageApp = hasScope('**', 'oauth2_app:update', ['oauth2_app:admin']) && isAppOwnedByAccount

  const handleTabSelect = (key: string | null) => {
    if (key && key !== activeTab) {
      navigate(
        {
          pathname: `/account/integration-settings/oauth2apps/${appId}/${key}`,
          search: searchParams.toString(),
        },
        { replace: false },
      )
    }
  }

  const handleManageAppClick = () => {
    navigate(`/account/integration-settings/oauth2apps/${appId}/manage/details`)
  }

  const handleDeveloperDocsClick = () => {
    window.open(DEVELOPER_DOCS_URL, '_blank', 'noopener,noreferrer')
  }

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  return (
    <>
      {headerContainer
        ? createPortal(
            <IntegrationManagementHeader
              backTarget={'app'}
              actions={
                canManageApp ? (
                  <>
                    {/* Temporarily hidden — will be re-enabled later */}
                    <Button variant='darkGhost' onClick={handleDeveloperDocsClick}>
                      Developer docs
                    </Button>
                    <Button variant='darkGhost' onClick={handleManageAppClick}>
                      <i className='bi bi-gear-fill me-1' />
                      Manage app
                    </Button>
                  </>
                ) : null
              }
            />,
            headerContainer,
          )
        : null}

      <Tabs className='mb-6' id='integration-management-tabs' defaultActiveKey={activeTab} activeKey={activeTab} onSelect={handleTabSelect}>
        <Tab eventKey='about' title='About'>
          <OAuth2AppsAboutTab description={description} />
        </Tab>

        <Tab eventKey='permissions' title='Permissions'>
          {activeTab === 'permissions' && <OAuth2AppsPermissionsTab oAuth2App={oAuth2App} />}
        </Tab>
      </Tabs>
    </>
  )
}
